import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticle } from '../lib/api';
import { SEO, buildArticleSchema, buildBreadcrumbSchema } from '../components/SEO';
import { ArrowLeft, Clock } from 'lucide-react';

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getArticle(slug).then(res => { setArticle(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="text-slate-500">Article not found</p>
        <Link to="/guides" className="text-blue-600 hover:text-blue-700 text-sm font-medium">Back to Guides</Link>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <SEO
        title={article.title}
        description={article.excerpt}
        canonical={`/guides/${article.slug}`}
        image={article.image_url}
        type="article"
        jsonLd={[
          buildArticleSchema(article),
          buildBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Guides', url: '/guides' },
            { name: article.title }
          ])
        ]}
      />
      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/guides" className="inline-flex items-center text-xs text-slate-400 hover:text-slate-300 mb-4 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Guides
          </Link>
          <span className="block text-xs font-medium text-blue-400 uppercase tracking-wider mb-2">{article.category}</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-['Outfit'] mb-4">{article.title}</h1>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Clock className="w-4 h-4" />
            <span>{article.reading_time}</span>
          </div>
        </div>
      </section>

      {article.image_url && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="aspect-[21/9] rounded-xl overflow-hidden border border-slate-200">
            <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article
          className="prose prose-slate prose-sm sm:prose-base max-w-none
            prose-headings:font-['Outfit'] prose-headings:tracking-tight
            prose-h2:text-2xl prose-h2:font-semibold prose-h2:text-slate-900 prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:font-medium prose-h3:text-slate-800 prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-slate-700 prose-p:leading-relaxed
            prose-li:text-slate-700
            prose-strong:text-slate-900
            prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        <div className="mt-12 pt-8 border-t border-slate-200">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <p className="text-xs text-slate-500 leading-relaxed">
              <strong className="text-slate-600">Disclaimer:</strong> This article is for educational and informational purposes only and does not constitute financial advice.
              Trading CFDs involves significant risk of loss. Always consult a qualified financial advisor before making trading decisions.
            </p>
          </div>
        </div>

        <div className="mt-8 flex justify-between items-center">
          <Link to="/guides" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All Guides
          </Link>
          <Link to="/compare" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
            Compare Brokers
          </Link>
        </div>
      </div>
    </div>
  );
}
